import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { storesApi } from '../../../api/stores.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useMfaStepUp } from '../../../hooks/useMfaStepUp.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { MfaPrompt } from '../../../components/MfaPrompt.jsx';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

import { StoreFormModal } from './StoreFormModal.jsx';

export function StoresPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const { run, mfaProps } = useMfaStepUp();

  const canCreate = hasPermission('stores:create');
  const canUpdate = hasPermission('stores:update');
  const canDelete = hasPermission('stores:delete');

  const { page, setPage, items, pagination, isLoading, filters, setFilters } = usePaginatedQuery({
    key: ['stores', 'list'],
    fetcher: storesApi.list
  });

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: (id) => run(() => storesApi.remove(id)),
    onSuccess: () => {
      toast.success('Negozio eliminato.');
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      setConfirmTarget(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const columns = [
    { key: 'code', header: 'Codice', render: (r) => <code className="rounded bg-border/40 px-1.5 py-0.5 text-xs">{r.code}</code> },
    { key: 'name', header: 'Nome' },
    { key: 'city', header: 'Città', render: (r) => r.city || '-' },
    { key: 'province', header: 'Provincia', render: (r) => r.province || '-' },
    {
      key: 'active',
      header: 'Stato',
      render: (r) => <Badge tone={r.active ? 'success' : 'neutral'}>{r.active ? 'Attivo' : 'Disattivato'}</Badge>
    },
    {
      key: 'createdAt',
      header: 'Creato',
      render: (r) => <span className="text-xs text-ink-muted">{fmt.date(r.createdAt)}</span>
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-2">
          {canUpdate && (
            <button
              type="button"
              className="text-xs text-ink-muted hover:text-ink"
              onClick={(e) => { e.stopPropagation(); setEditing(r); setFormOpen(true); }}
            >
              Modifica
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="text-xs text-danger hover:underline"
              onClick={(e) => { e.stopPropagation(); setConfirmTarget(r); }}
            >
              Elimina
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('nav.stores')}
        description="Lista dei punti vendita e relativi orari di apertura."
        actions={
          canCreate ? (
            <Button onClick={() => { setEditing(null); setFormOpen(true); }}>Nuovo negozio</Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <Input
          className="w-64"
          label="Cerca"
          placeholder="Codice o nome..."
          value={filters.q || ''}
          onChange={(e) => setFilters({ q: e.target.value || undefined })}
        />
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{
          title: 'Nessun negozio',
          description: canCreate ? 'Crea il primo punto vendita.' : 'Nessun risultato.'
        }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <StoreFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        initial={editing}
      />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Eliminare il negozio?"
        description={confirmTarget ? `Il negozio "${confirmTarget.name}" sarà rimosso definitivamente.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => deleteMutation.mutate(confirmTarget._id)}
      />

      <MfaPrompt {...mfaProps} />
    </div>
  );
}
